import { BasicPagination } from "@/components/atom/pagination/basic/BasicPagination";
import { CalendarTrigger } from "@/components/molecule/calendarTrigger/CalendarTrigger";
import { MantineDataTable } from "@/components/molecule/mantineDataTable/MantineDataTable";
import { MantineGroupTable } from "@/components/molecule/mantineGroupTable/MantineGroupTable";
import ProductionStatusCard from "@/components/molecule/productionStatusCard/ProductionStatusCard";
import { StatusMultipleChipGroup } from "@/components/molecule/statusMultipleChipGroup/StatusMultipleChipGroup";
import {
  Item,
  ReorderableListForm,
} from "@/components/organism/form/ReorderableListForm";
import { WorksActionHeader } from "@/components/organism/header/refactor/worksActionHeader/WorksActionHeader";
import PageTemplate from "@/components/template/page/PageTemplate";
import { customFunctions } from "@/config/customFunctions";
import { COMMON_TEXT } from "@/constants/text";
import { trackingStatusInKorean } from "@/constants/trackingType";
import { useLocalStorage } from "@/context/LocalStorageProvider";
import { useModal } from "@/context/ModalStackManager";
import { useWorksOrderGroupFetch } from "@/fetch/page/userWorksOrderGroupFetch";
import { useUserFetch } from "@/fetch/user/useUserFetch";
import { useWorksOrderHandler } from "@/hooks/handler/useWorksOrderHandler";
import { useWorksSearchActions } from "@/hooks/useWorksActions.hook";
import { useWorksSearchState } from "@/hooks/useWorksState.hook";
import { Button, Flex, Select } from "@mantine/core"; // ✅ SegmentedControl 추가
import {
  WorksItemGet200ResponseRowsInner,
  WorksOrderGroupsGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

const Works = Object.assign(
  {},
  {
    Template: PageTemplate,
    CalendarTrigger: CalendarTrigger,
    StatusMultipleChipGroup: StatusMultipleChipGroup,
    WorksActionHeader: WorksActionHeader,
    DataTable: MantineDataTable,
    MantineGroupTable: MantineGroupTable,
    ProductionStatusCard: ProductionStatusCard,
    Select: Select,
    Pagination: BasicPagination,
  },
);

type ConvertedDataType = {
  id: number | undefined;
  scheduleSeq: string | number;
  equipmentName: string;
  itemName: string;
  targetQuantity: string;
  totalQuantity?: string;
  todoQuantity?: string;
  defectTotal?: string;
  spec: string;
  scheduledAt: string;
  trackingStatus: string | undefined;
  description: string | undefined;
  itemDescription?: string | undefined;
  custName?: string | undefined;
  dvPlaceName?: string | undefined;
  groupTargetQuantity?: string; // ✅ 누락된 필드 추가
};

export const WorksPage = () => {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { setWorkId } = useLocalStorage();
  const token = localStorage.getItem("authToken");
  const { data: user } = useUserFetch({ token: token ?? "", enabled: !!token });
  const userData = user?.data;
  const equipmentCode = userData?.equipmentCode;
  const { data: worksOrderGroupData, Fetcher: WorksOrderGroupFetch } =
    useWorksOrderGroupFetch({ equipmentCode });

  const { onSubmit } = useWorksOrderHandler();

  const { dates, custName } = useWorksSearchState();
  const { setDates, setCustName } = useWorksSearchActions();

  const items: Item[] =
    worksOrderGroupData?.data.map((item) => {
      const label =
        item.custName && item.dvPlaceName
          ? `고객명: ${item.custName ?? "-"} | 납품처: ${
              item.dvPlaceName ?? "-"
            } | 설비: ${item.equipmentName ?? "-"} | 총수량 : ${
              item.works?.[0].groupTargetQuantity ?? "-"
            }`
          : `품목명: ${item.works?.[0].item?.name ?? "-"} | 계획수량: ${
              item.works?.[0].targetQuantity ?? "-"
            } | 총수량 : ${item.works?.[0].targetQuantity ?? "-"} `;

      return {
        label,
        id: item.code as string,
      };
    }) ?? [];

  const custNameSelect = useMemo(() => {
    if (!worksOrderGroupData?.data) return [];
    const uniqueCustNames = Array.from(
      new Set(
        worksOrderGroupData.data
          .map((item) => item.custName)
          .filter((name): name is string => !!name),
      ),
    );
    return uniqueCustNames.map((name) => ({
      value: name,
      label: name,
    }));
  }, [worksOrderGroupData?.data]);

  const groupData = worksOrderGroupData?.data;

  const isGroupDisabled = !worksOrderGroupData?.data.find((d) => d.custName);

  const convertedGroupData =
    groupData?.map((g, index) => ({
      ...g,
      scheduleSeq: index + 1,
      custName: g.custName ?? "-",
      dvPlaceName: g.dvPlaceName ?? "-",
      groupTotalQuantity:
        g.custName && g.dvPlaceName
          ? `${g.works?.[0].groupTotalQuantity}${
              g.works?.[0].item?.unitText ?? ""
            }`
          : "",
      scheduledAt: dayjs(g.createdDay).format("YYYY-MM-DD") ?? "-",
      trackingStatus:
        trackingStatusInKorean[
          g.status as keyof typeof trackingStatusInKorean
        ] ?? "-",
    })) ?? [];

  const onClickMemorize = (data: ConvertedDataType) => setWorkId(data.id);

  // 업체명과 현장명이 같은것만 비규격 POP다.
  // 업체명만 같아서는 비규격이 아니다.
  // 업체명은 무조건 있어야 하고, 현장명은 없을 수도 있다.
  const onClickNavigate = (data: ConvertedDataType) => {
    const isWireRod = data.itemName?.includes("선재(반제품)1");
    const customerAndPlace = data.dvPlaceName === "-";
    const id = data.id;

    if (isWireRod) return navigate(`/wireRode/${id}`);
    if (customerAndPlace) return navigate(`/work/${id}`);
    return navigate(`/nonStandardWork/${id}`);
  };

  const onClickOrder = async (items: Item[]) => {
    onSubmit({
      worksOrderGroupOrderSequencePutRequest: {
        codes: items.map((item) => item.id),
        createdDay: dates[0]?.toString() as string,
      },
    });
  };

  const columns = () => [
    {
      accessorKey: "scheduleSeq",
      header: COMMON_TEXT.COLUMN.SCHEDULE_SEQ,
      maxSize: 125,
    },
    {
      accessorKey: "custName",
      header: COMMON_TEXT.COLUMN.CUSTNAME,
      maxSize: 110,
    },
    {
      accessorKey: "dvPlaceName",
      header: COMMON_TEXT.COLUMN.DV_PLACE_NAME,
      maxSize: 110,
    },
    {
      accessorKey: "equipmentName",
      header: COMMON_TEXT.COLUMN.EQUIPMENT_NAME,
      maxSize: 130,
    },
    {
      accessorKey: "itemName",
      header: `${COMMON_TEXT.COLUMN.ITEM_NAME}(${COMMON_TEXT.COLUMN.ROUTING_CODE})`,
      maxSize: 120,
    },
    {
      accessorKey: "targetQuantity",
      header: COMMON_TEXT.COLUMN.TARGET_QUANTITY,
      cell: ({ cell }: any) => {
        return (
          <Flex w="100%" justify={"flex-end"}>
            <span>{cell.getValue()}</span>
          </Flex>
        );
      },
      maxSize: 125,
    },
    ...(customFunctions.ADD_WORK_ADDITIONAL_COLUMN
      ? [
          {
            accessorKey: "totalQuantity",
            header: COMMON_TEXT.COLUMN.TOTAL_QUANTITY,
          },
          {
            accessorKey: "todoQuantity",
            header: COMMON_TEXT.COLUMN.TODO_QUANTITY,
          },
          {
            accessorKey: "defectTotal",
            header: COMMON_TEXT.COLUMN.DEFECT_TOTAL,
          },
        ]
      : []),
    {
      accessorKey: "groupTotalQuantity",
      header: COMMON_TEXT.COLUMN.GROUP_TARGET_QUANTITY,
      maxSize: 115,
    },
    {
      accessorKey: "scheduledAt",
      header: COMMON_TEXT.COLUMN.SCHEDULED_AT,
      maxSize: 115,
    },
    {
      accessorKey: "trackingStatus",
      header: COMMON_TEXT.COLUMN.TRACKING_STATUS,
      maxSize: 130,
    },
  ];

  function convertWorksData(
    worksData: WorksItemGet200ResponseRowsInner[] | undefined,
  ): ConvertedDataType[] {
    if (!worksData) return [];

    return worksData.map((workData) => {
      const id = workData?.id;
      const scheduleSeq = workData?.scheduleSeq ?? "-";
      const trackingStatus =
        workData?.trackingStatus !== undefined
          ? (trackingStatusInKorean[workData.trackingStatus] ?? "알 수 없음")
          : "알 수 없음";
      const equipmentName = workData?.equipment?.name ?? "-";
      const itemName = `${workData.item?.name ?? "-"}(${
        workData?.routingCode ?? "-"
      })`;
      const targetQuantity = workData?.targetQuantity
        ? `${workData?.targetQuantity}${workData.item?.unitText ?? ""}`
        : `-`;
      const spec = workData?.spec ?? "-";
      const scheduledAt = workData?.scheduledAt
        ? dayjs(workData.scheduledAt).format("YYYY-MM-DD")
        : "-";
      const description = workData.description;
      const itemDescription = workData.itemDescription;
      const dvPlaceName = workData.dvPlaceName ?? "-";
      const custName = workData.custName ?? "-";
      const groupTotalQuantity =
        workData.custName && workData.dvPlaceName
          ? `${workData.groupTotalQuantity}${workData.item?.unitText ?? ""}`
          : `${workData.targetQuantity}${workData.item?.unitText ?? ""}`;

      return {
        id,
        scheduleSeq,
        equipmentName,
        itemName,
        targetQuantity,
        spec,
        scheduledAt,
        trackingStatus,
        description,
        itemDescription,
        dvPlaceName,
        custName,
        groupTotalQuantity,
      };
    });
  }

  return (
    <Works.Template>
      <Flex direction="column" gap="md">
        <Works.CalendarTrigger
          mode="single"
          value={dates[0]}
          onChange={(v) => {
            setDates([v, v]);
            setCustName("");
          }}
          size="1.5rem"
        />
        <Flex gap={"md"}>
          <Select
            disabled={isGroupDisabled}
            size="sm"
            placeholder={COMMON_TEXT.PLACEHOLDER.CustName}
            onChange={(name) => setCustName(name ?? "")}
            data={custNameSelect ?? []}
            value={custName}
            clearable
            searchable
          />
          <Button
            onClick={async () => {
              await openModal(
                <ReorderableListForm
                  initialItems={items}
                  onClick={onClickOrder}
                />,
                null,
                COMMON_TEXT.BUTTON.WORK_ORDER,
              );
            }}
          >
            {COMMON_TEXT.BUTTON.WORK_ORDER}
          </Button>
        </Flex>
        <WorksOrderGroupFetch>
          <MantineGroupTable<
            WorksOrderGroupsGet200ResponseRowsInner,
            ConvertedDataType
          >
            onRowClick={(group) => {
              const converted = convertWorksData(group.works);
              if (converted.length > 0) {
                onClickNavigate(converted[0]);
                onClickMemorize(converted[0]);
              }
            }}
            data={convertedGroupData}
            columns={columns()}
            getSubRows={(row) => convertWorksData(row.works) ?? []}
          />
        </WorksOrderGroupFetch>
      </Flex>
    </Works.Template>
  );
};
